/**
 * What every sign-in surface in this app is given — `LoginPage` on the login
 * route and `LoginModal` wherever signing in interrupts something else.
 *
 * One hook so the page and the modal are wired to the same Firebase auth,
 * offer the same providers (email and Google) and say the same words. The
 * handlers keep the analytics the login page has always sent: a click event
 * when an attempt starts and an error event when one fails. The error is then
 * re-thrown, so the shared form still shows it inline.
 *
 * @module
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirebaseAuth } from '@sudobility/auth_lib';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  type Auth,
} from 'firebase/auth';
import type { LoginPageText } from '@sudobility/building_blocks';
import type { LoginModalText } from '@sudobility/components';
import { trackButtonClick, trackError } from '../analytics';

/** Runs `action` with this app's analytics around it. */
async function tracked(event: string, fallbackMessage: string, action: () => Promise<unknown>) {
  trackButtonClick(event);
  try {
    await action();
  } catch (err) {
    trackError(err instanceof Error ? err.message : fallbackMessage, `${event}_error`);
    throw err;
  }
}

export function useSignInProps() {
  const { t } = useTranslation('auth');
  const auth = getFirebaseAuth();

  return useMemo(() => {
    // With Firebase unconfigured there is nothing to sign in to: the form says
    // so inline instead of failing silently.
    const withAuth = (run: (a: Auth) => Promise<unknown>) => () => {
      if (!auth) return Promise.reject(new Error(t('firebaseNotConfigured')));
      return run(auth);
    };

    const text: Partial<LoginPageText> = {
      signIn: t('signIn'),
      signUp: t('signUp'),
      emailLabel: t('email'),
      emailPlaceholder: t('emailPlaceholder'),
      passwordLabel: t('password'),
      passwordPlaceholder: t('passwordPlaceholder'),
      orContinueWith: t('orContinueWith'),
      signInWithGoogle: t('continueWithGoogle'),
      alreadyHaveAccount: t('haveAccount'),
      dontHaveAccount: t('noAccount'),
      genericError: t('genericError'),
      forgotPassword: t('forgotPassword'),
      resetPasswordHint: t('resetPasswordHint'),
      sendResetLink: t('sendResetLink'),
      resetEmailSent: t('resetEmailSentNotice'),
      backToSignIn: t('backToSignIn'),
      signInToAccount: t('signInToAccount'),
      createAccount: t('createAccount'),
      resetPassword: t('resetPassword'),
    };
    const modalText: Partial<LoginModalText> = {
      signInTitle: t('signIn'),
      signUpTitle: t('createAccount'),
      resetPasswordTitle: t('resetPassword'),
      close: t('close'),
    };

    return {
      /** False when Firebase is not configured. */
      available: !!auth,
      onEmailSignIn: (email: string, password: string) =>
        tracked(
          'email_sign_in',
          'Email sign-in failed',
          withAuth(a => signInWithEmailAndPassword(a, email, password))
        ),
      onEmailSignUp: (email: string, password: string) =>
        tracked(
          'email_sign_up',
          'Email sign-up failed',
          withAuth(a => createUserWithEmailAndPassword(a, email, password))
        ),
      onPasswordReset: (email: string) =>
        tracked(
          'password_reset',
          'Password reset failed',
          withAuth(a => sendPasswordResetEmail(a, email))
        ),
      onGoogleSignIn: () =>
        tracked(
          'google_sign_in',
          'Google sign-in failed',
          withAuth(a => signInWithPopup(a, new GoogleAuthProvider()))
        ),
      text,
      modalText,
    };
  }, [t, auth]);
}
